/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0 authorization code flow.
 * 
 * PKCE adds security to the authorization code flow by using a code verifier and code challenge:
 * 1. Client generates a random code_verifier (43-128 chars)
 * 2. Client creates code_challenge = base64url(SHA256(code_verifier))
 * 3. Client sends code_challenge to authorization endpoint
 * 4. After auth, client sends code_verifier to token endpoint
 * 5. Server verifies SHA256(code_verifier) === code_challenge
 */

const VERIFIER_STORAGE_KEY = 'sl-pkce-verifier';

/**
 * Generate a cryptographically random code verifier.
 * Length: 43-128 characters (we use 64 for good entropy)
 * Characters: [A-Z] [a-z] [0-9] - . _ ~
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(48);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate code challenge from code verifier using SHA-256.
 * code_challenge = base64url(SHA256(code_verifier))
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64 URL encoding (RFC 4648 §5).
 * Replaces + with -, / with _, removes padding =
 */
function base64UrlEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Store the code verifier in sessionStorage for later retrieval.
 */
export function storeCodeVerifier(verifier: string): void {
  sessionStorage.setItem(VERIFIER_STORAGE_KEY, verifier);
}

/**
 * Retrieve and remove the code verifier from sessionStorage.
 */
export function retrieveCodeVerifier(): string | null {
  const verifier = sessionStorage.getItem(VERIFIER_STORAGE_KEY);
  if (verifier) {
    sessionStorage.removeItem(VERIFIER_STORAGE_KEY);
  }
  return verifier;
}

/**
 * Clear the stored code verifier.
 */
export function clearCodeVerifier(): void {
  sessionStorage.removeItem(VERIFIER_STORAGE_KEY);
}

/**
 * Generate a PKCE pair (verifier + challenge) and store the verifier.
 */
export async function generateAndStorePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  storeCodeVerifier(codeVerifier);
  return { codeVerifier, codeChallenge };
}
